import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useMemo } from "react";
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridCellParams,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import ChipWithCount from "../../../components/ChipWithCount";
import type { LifecycleEventDetailsGridProps } from "./types";
import { getLifecycleEventDetailsColumns } from "./lifecycleEventDetailsColumns";
import { defaultFormatCurrency } from "./utils";
import { groupLineItemsForDisplay } from "./groupLineItemsForDisplay";
import { InlineTreeToggleCell } from "./InlineTreeToggleCell";

export function LifecycleEventDetailsGrid({
  lineItems,
  total,
  isLoading,
  isError,
  onRetry,
  formatCurrency = defaultFormatCurrency,
  renderHeaderActions,
  renderGrid,
}: Readonly<LifecycleEventDetailsGridProps>) {
  const { rows, hasGroups } = useMemo(
    () => groupLineItemsForDisplay(lineItems),
    [lineItems],
  );

  const columns = useMemo<GridColDef[]>(() => {
    const base = getLifecycleEventDetailsColumns(formatCurrency);
    if (!hasGroups) return base;
    return base.map((col) => {
      if (col.field === "manufacturerPartNumber") {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => (
            <InlineTreeToggleCell
              params={params}
              expandLabel="Expand units"
              collapseLabel="Collapse units"
            />
          ),
        };
      }
      if (col.field === "serialNumber") {
        return {
          ...col,
          cellClassName: (params: GridCellParams) =>
            params.row._isSerialGroup ? "serial-group-label" : "",
        };
      }
      return col;
    });
  }, [formatCurrency, hasGroups]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress color="inherit" size={50} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
          mt: 4,
          px: 2,
        }}
      >
        <Typography variant="body2">
          There has been an error loading the details right now. Please try
          again later.
        </Typography>
        <Button onClick={onRetry} size="small" variant="outlined">
          Retry
        </Button>
      </Box>
    );
  }

  // Imported events have line items but no SO number, so orders can legitimately be 0.
  if (total.lineItems === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
          mt: 4,
          px: 2,
        }}
      >
        <Typography variant="body2">
          No line items are associated with this event.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, p: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <ChipWithCount
            singularLabel="Order"
            pluralLabel="Orders"
            count={total.orders}
          />
          <ChipWithCount
            singularLabel="Covered Asset"
            pluralLabel="Covered Assets"
            count={total.lineItems}
          />
          <ChipWithCount
            singularLabel="Previous Purchase Cost"
            pluralLabel="Previous Purchase Cost"
            count={formatCurrency(total.previousPurchaseCost)}
          />
        </Box>
        {renderHeaderActions?.()}
      </Box>
      <Box
        sx={{
          borderTop: "1px solid #D0D0D0",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          "& .serial-group-label": {
            fontStyle: "italic",
            color: "text.secondary",
          },
        }}
      >
        {renderGrid({
          columnDefinitions: columns,
          rows,
          isLoading,
          hiddenColumns: hasGroups
            ? [GRID_TREE_DATA_GROUPING_FIELD]
            : undefined,
          treeData: hasGroups,
        })}
      </Box>
    </Box>
  );
}
